/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAlert } from '../../features/slices/alertSlice';
import {
    addFacilityLabServices,
    getFacilityById,
    removeFacilityLabServices,
} from '../../features/slices/facilitySlice';
import {
    getAllLabOrdersMasterList,
    saveLabMaster,
} from '../../features/slices/labDiagnosticSlice';
import { AppDispatch, RootState } from '../../features/store';
import { IFacilityLabService } from '../../features/types/facility.types';
import HCInput from '../Input';
import MultiSelectInput from '../MultiSelect';
import AddLabServiceModal from './AddLabServiceModal';

interface ILabOptionType {
    label: string;
    value: number;
    delete: boolean;
    block: boolean;
}

const AddLabServicesForm = ({
    edit,
}: {
    edit: boolean;
    onClose?: () => void;
}) => {
    const { selectedFacility, loading } = useSelector(
        (state: RootState) => state.facility,
    );
    const { labOrderMaterList } = useSelector(
        (state: RootState) => state.labDiagnostic,
    );
    const { data: user } = useSelector((state: RootState) => state.auth);
    const [addLabOpen, setAddLabOpen] = useState(false);
    const [labFees, setLabFees] = useState<
        { id: number; labServiceId: number; label: string; fees: string }[]
    >([]);

    const isServiceAdmin =
        user?.UserRole === 'SuperAdmin' ||
        user?.UserRole === 'SystemAdministrator';

    const dispatch = useDispatch<AppDispatch>();
    useEffect(() => {
        // Load global LabDiagnosticMaster when Redux list is empty (View + Edit)
        if (labOrderMaterList?.length) {
            return;
        }
        dispatch(getAllLabOrdersMasterList());
    }, [dispatch, labOrderMaterList?.length]);

    useEffect(() => {
        const assigned = (selectedFacility?.facilityLabServices || []).filter(
            (i: IFacilityLabService) => !i?.isDelete,
        );
        setLabFees(
            assigned.map((i: IFacilityLabService) => {
                const master = labOrderMaterList?.find(
                    (m) => m.id === i.labServiceId,
                );
                const fee = i.fees ?? master?.fees ?? '';
                return {
                    id: i.id,
                    labServiceId: i.labServiceId || master?.id || 0,
                    label: `${i.facilityLabService} (${i.facilityLabSubService})`,
                    fees: fee === null || fee === undefined ? '' : String(fee),
                };
            }),
        );
    }, [selectedFacility?.facilityLabServices, labOrderMaterList]);

    const handleSelectedLabServicesChange = (newValues: ILabOptionType[]) => {
        const filteredValues = selectedFacility.facilityLabServices.map(
            (i) => i.id,
        );
        const payload = newValues
            .filter((i) => !filteredValues.includes(i.value))
            .map((i) => ({
                labServiceId: i.value,
                facilityId: selectedFacility?.facilityProfile?.id,
                createdBy: user?.UserID,
            }));
        if (!payload.length) {
            return;
        }
        dispatch(addFacilityLabServices(payload)).then((res) => {
            if (res.payload?.responseCode === '200') {
                dispatch(
                    getFacilityById(selectedFacility?.facilityProfile?.userId),
                );
            }
        });
    };

    const onRemoveLabServices = (obj: ILabOptionType) => {
        const payload = {
            facilityId: selectedFacility?.facilityProfile?.id,
            labServiceId: obj.value,
            createdBy: user?.UserID,
        };
        dispatch(removeFacilityLabServices([payload])).then((res) => {
            if (res.payload?.responseCode === '200') {
                dispatch(
                    getFacilityById(selectedFacility?.facilityProfile?.userId),
                );
            }
        });
    };

    const assignLabByMasterId = (labServiceId: number) => {
        dispatch(
            addFacilityLabServices([
                {
                    labServiceId,
                    facilityId: selectedFacility?.facilityProfile?.id,
                    createdBy: user?.UserID,
                },
            ]),
        ).then((res) => {
            if (res.payload?.responseCode === '200') {
                dispatch(
                    getFacilityById(selectedFacility?.facilityProfile?.userId),
                );
            }
        });
    };

    const onLabCreated = (labServiceId: number) => {
        dispatch(getAllLabOrdersMasterList()).then(() => {
            assignLabByMasterId(labServiceId);
        });
    };

    const onChangeLabFee = (value: string, labServiceId: number) => {
        setLabFees((prev) =>
            prev.map((i) =>
                i.labServiceId === labServiceId ? { ...i, fees: value } : i,
            ),
        );
    };

    const onSaveLabFees = (e: React.FormEvent) => {
        e.preventDefault();
        const assigned = (selectedFacility?.facilityLabServices || []).filter(
            (i: IFacilityLabService) => !i?.isDelete,
        );
        const updates = labFees.filter((feeRow) => feeRow.labServiceId > 0);
        if (!updates.length) {
            return;
        }

        Promise.all(
            updates.map((feeRow) => {
                const assignedRow = assigned.find(
                    (i: IFacilityLabService) =>
                        i.labServiceId === feeRow.labServiceId ||
                        i.id === feeRow.id,
                );
                return dispatch(
                    saveLabMaster({
                        id: feeRow.labServiceId,
                        labServiceArea:
                            assignedRow?.facilityLabService || '',
                        labServiceSubArea:
                            assignedRow?.facilityLabSubService || '',
                        fees:
                            feeRow.fees.trim() === ''
                                ? null
                                : Number(feeRow.fees),
                        createdBy: user?.UserID,
                    }),
                );
            }),
        ).then((results: any[]) => {
            const failed = results.some(
                (res) => res?.payload?.responseCode !== '200',
            );
            if (failed) {
                dispatch(
                    setAlert({
                        message: 'Unable to save one or more lab service fees',
                        severity: 'error',
                    }),
                );
                return;
            }
            dispatch(
                setAlert({
                    message: 'Lab service fees saved',
                    severity: 'success',
                }),
            );
            dispatch(getAllLabOrdersMasterList());
            dispatch(
                getFacilityById(selectedFacility?.facilityProfile?.userId),
            );
        });
    };

    return (
        <div>
            {selectedFacility.facilityProfile?.isLab ? (
                <>
                    <MultiSelectInput<ILabOptionType>
                        selectedValues={selectedFacility?.facilityLabServices
                            ?.filter((i) => !i?.isDelete)
                            ?.map((i) => ({
                                value: i.id,
                                label: `${i.facilityLabService} (${i.facilityLabSubService})`,
                                delete: i.isDelete,
                                block: i.isBlocked,
                            }))}
                        options={labOrderMaterList
                            .filter((i) => !i?.isDelete && !i?.isBlocked)
                            .map((i) => ({
                                value: i.id,
                                label: `${i.labServiceArea} (${i.labServiceSubArea})`,
                                delete: i?.isDelete,
                                block: i?.isBlocked,
                            }))}
                        handleDelete={onRemoveLabServices}
                        title="Select Lab Services"
                        getOptionLabel={(option) => option.label}
                        placeholder="Select Lab Services"
                        disabled={loading === 'pending'}
                        disableAll={edit}
                        onSelectedValuesChange={handleSelectedLabServicesChange}
                        addNewLabel={
                            isServiceAdmin && !edit
                                ? '➕ Add New Lab Service'
                                : undefined
                        }
                        onAddNew={
                            isServiceAdmin && !edit
                                ? () => setAddLabOpen(true)
                                : undefined
                        }
                    />
                    {addLabOpen ? (
                        <AddLabServiceModal
                            isOpen={addLabOpen}
                            onClose={() => setAddLabOpen(false)}
                            onCreated={onLabCreated}
                        />
                    ) : null}
                    {labFees.length > 0 ? (
                        <form onSubmit={onSaveLabFees} style={{ marginTop: 12 }}>
                            <Grid container spacing={2}>
                                {labFees.map((i) => (
                                    <Grid item md={6} xs={12} key={i.id}>
                                        <HCInput
                                            name={`lab-fee-${i.labServiceId || i.id}`}
                                            label={`${i.label} — Lab Service Fee`}
                                            placeholder="Lab Service Fee"
                                            type="number"
                                            min={0}
                                            max={100000}
                                            value={i.fees}
                                            onChange={(e) =>
                                                onChangeLabFee(
                                                    e.target.value,
                                                    i.labServiceId,
                                                )
                                            }
                                            disabled={edit || !isServiceAdmin}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            {!edit && isServiceAdmin ? (
                                <Button
                                    variant="contained"
                                    type="submit"
                                    style={{
                                        marginTop: '8px',
                                        float: 'right',
                                        width: 'fit-content',
                                    }}
                                    disabled={loading === 'pending'}
                                >
                                    Save Lab Service Fees
                                </Button>
                            ) : null}
                        </form>
                    ) : null}
                </>
            ) : null}
        </div>
    );
};

export default AddLabServicesForm;
